import { useQuery } from '@tanstack/react-query'
import type { MapAddressResult } from '@/features/geolocation/utils/map-search-utils'
import { useDebounced } from '@/hooks/use-debounced'
import { extensionsApi } from '@/services/api/extensions'

const ADDRESS_SEARCH_MIN_LENGTH = 3
const ADDRESS_SEARCH_DEBOUNCE_MS = 300
const ADDRESS_RESULT_LIMIT = 10

export type AddressSearchStatus = 'idle' | 'short' | 'loading' | 'ready' | 'error'

export interface AddressSearchState {
  status: AddressSearchStatus
  results: MapAddressResult[]
}

/** Debounced IO boundary for the map's remote address candidates. */
export function useMapAddressSearch(
  query: string,
  isCoordinate: boolean,
  active: boolean
): AddressSearchState {
  const trimmed = query.trim()
  const debounced = useDebounced(trimmed, ADDRESS_SEARCH_DEBOUNCE_MS)
  const eligible = active && !isCoordinate && trimmed.length >= ADDRESS_SEARCH_MIN_LENGTH
  const caughtUp = debounced === trimmed
  const lookup = useQuery({
    queryKey: ['map-address-search', debounced],
    queryFn: () => extensionsApi.geocode(debounced, ADDRESS_RESULT_LIMIT),
    enabled: eligible && caughtUp,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })

  if (!active || !trimmed || isCoordinate) return { status: 'idle', results: [] }
  if (trimmed.length < ADDRESS_SEARCH_MIN_LENGTH) return { status: 'short', results: [] }
  if (!caughtUp || lookup.isFetching) return { status: 'loading', results: [] }
  if (lookup.isError) return { status: 'error', results: [] }
  return { status: 'ready', results: lookup.data?.results ?? [] }
}
